import { useState } from 'react';
import { Calendar, Home, IndianRupee, FileText, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function CreateListingForm({ currentUser, onSuccess }) {
  const [formData, setFormData] = useState({
    off_season_months: [],
    rooms_available: '',
    minimum_rent: '',
    optional_notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const toggleMonth = (month) => {
    setFormData(prev => ({
      ...prev,
      off_season_months: prev.off_season_months.includes(month)
        ? prev.off_season_months.filter(m => m !== month)
        : [...prev.off_season_months, month]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/off-season/listings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resort_id: currentUser.id,
          resort_name: currentUser.name,
          resort_email: currentUser.email,
          resort_phone: currentUser.phone,
          location: currentUser.location || 'India',
          state: currentUser.state || 'India',
          off_season_months: formData.off_season_months,
          rooms_available: parseInt(formData.rooms_available),
          minimum_rent: parseInt(formData.minimum_rent),
          optional_notes: formData.optional_notes
        })
      });

      const data = await response.json();

      if (data.id) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        setError(data.error || 'Failed to create listing');
      }
    } catch (err) {
      setError('Failed to create listing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="h-10 w-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Listing Published!</h2>
          <p className="text-gray-600">Your off-season availability is now visible to other resorts.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create Off-Season Listing</CardTitle>
        <p className="text-sm text-gray-600 mt-2">
          Select the months you want guaranteed revenue and enter your availability details.
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Month Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              <Calendar className="h-4 w-4 inline mr-2" />
              Off-Season Months *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {MONTHS.map((month) => (
                <Badge
                  key={month}
                  className={`cursor-pointer py-3 justify-center text-sm ${
                    formData.off_season_months.includes(month)
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => toggleMonth(month)}
                >
                  {month}
                </Badge>
              ))}
            </div>
            {formData.off_season_months.length > 0 && (
              <p className="text-xs text-emerald-600 mt-2">
                {formData.off_season_months.length} month(s) selected
              </p>
            )}
          </div>

          {/* Rooms Available */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Home className="h-4 w-4 inline mr-2" />
              Number of Rooms Available *
            </label>
            <Input
              type="number"
              value={formData.rooms_available}
              onChange={(e) => setFormData({ ...formData, rooms_available: e.target.value })}
              placeholder="e.g., 10"
              required
              min="1"
            />
          </div>

          {/* Minimum Rent */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <IndianRupee className="h-4 w-4 inline mr-2" />
              Minimum Rent Expected (per room/night) *
            </label>
            <Input
              type="number"
              value={formData.minimum_rent}
              onChange={(e) => setFormData({ ...formData, minimum_rent: e.target.value })}
              placeholder="e.g., 2000"
              required
              min="0"
            />
            <p className="text-xs text-gray-500 mt-1">This is informational only - final price is negotiated directly</p>
          </div>

          {/* Optional Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="h-4 w-4 inline mr-2" />
              Optional Notes
            </label>
            <textarea
              value={formData.optional_notes}
              onChange={(e) => setFormData({ ...formData, optional_notes: e.target.value })}
              placeholder="Include details about F&B, spa, activities, or any special terms..."
              rows="4"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Other resorts will see your listing and can contact you directly. Travelodges does not handle payments or guest bookings.
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3">
            <Button
              type="submit"
              disabled={loading || formData.off_season_months.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {loading ? 'Publishing...' : 'Publish Listing'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default CreateListingForm;
